import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';

const useLifeEventApi = (queryParams) => {
  const { t, i18n } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/life-events?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const updateStatus = useCallback(async (id, status, notes) => {
    try {
      await api.put(`/v1/life-events/${id}/status`, { status, notes });
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const create = useCallback(async (formData) => {
    try {
      const payload = {
        employeeId: formData.employeeId,
        eventType: formData.eventType,
        eventDate: formData.eventDate,
        notes: formData.notes || null,
        documentUrl: formData.documentUrl || null,
      };
      await api.post('/v1/life-events', payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const eventTypeLabels = useMemo(() => ({
    marriage: t('lifeEvent.typeMarriage'),
    'birth-or-adoption': t('lifeEvent.typeBirthOrAdoption'),
    divorce: t('lifeEvent.typeDivorce'),
    'spouse-job-loss': t('lifeEvent.typeSpouseJobLoss'),
    'parent-death': t('lifeEvent.typeParentDeath'),
  }), [t]);

  const statusLabels = useMemo(() => ({
    submitted: t('lifeEvent.statusSubmitted'),
    acknowledged: t('lifeEvent.statusAcknowledged'),
    resolved: t('lifeEvent.statusResolved'),
    expired: t('lifeEvent.statusExpired'),
  }), [t]);

  const statusOptions = useMemo(() => Object.entries(statusLabels).map(([id, name]) => ({ id, name })), [statusLabels]);
  const eventTypeOptions = useMemo(() => Object.entries(eventTypeLabels).map(([id, name]) => ({ id, name })), [eventTypeLabels]);

  const objectSchema = useMemo(() => [
    {
      name: 'employeeId', label: t('lifeEvent.employee'),
      type: 'asyncSelect', grid: 12, required: true,
      onSearch: async (q) => {
        const params = { pageSize: 30 };
        if (q) params['fullName[like]'] = q;
        const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
        const r = res.data?.data;
        const items = Array.isArray(r) ? r : (r?.data || []);
        return items.map((it) => ({ id: it.id, name: `${it.firstName || ''} ${it.lastName || ''}`.trim() || it.email, profilePicture: it.profilePicture }));
      },
      onGetById: async (id) => {
        try {
          const res = await api.get(`/v1/employees/${id}`);
          const e = res.data?.data;
          return { id, name: e ? `${e.firstName || ''} ${e.lastName || ''}`.trim() : '', profilePicture: e?.profilePicture };
        } catch { return { id, name: '', profilePicture: null }; }
      },
    },
    {
      name: 'eventType', label: t('lifeEvent.eventType'),
      type: 'select', grid: 6, required: true,
      options: eventTypeOptions, idKey: 'id', labelKey: 'name',
    },
    { name: 'eventDate', label: t('lifeEvent.eventDate'), type: 'date', grid: 6, required: true },
    { name: 'notes', label: t('lifeEvent.notes'), type: 'textarea', grid: 12 },
  ], [t, eventTypeOptions]);

  const deadlineCol = useMemo(() => {
    const base = dateFilterColumn('applicationDeadline', t('lifeEvent.deadline'), i18n.language);
    return {
      ...base,
      options: {
        ...base.options,
        customBodyRender: (v, tableMeta) => {
          if (!v) return '-';
          const row = data[tableMeta.rowIndex];
          const overdue = row?.isExpired || (new Date(v) < new Date() && row?.status === 'submitted');
          return (
            <span style={{ color: overdue ? '#d32f2f' : 'inherit', fontWeight: overdue ? 600 : 400 }}>
              {new Date(v).toLocaleDateString('tr-TR')}
            </span>
          );
        },
      },
    };
  }, [t, i18n.language, data]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'employeeFullName', label: t('lifeEvent.employee'),
      options: {
        customBodyRender: (v, tableMeta) => (
          <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.employeeProfilePicture} />
        ), filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    {
      name: 'eventType', label: t('lifeEvent.eventType'),
      options: {
        filter: true, sort: true, filterType: 'dropdown',
        filterOptions: { defaultOp: 'eq', names: eventTypeOptions.map((o) => o.name), renderValue: (v) => v },
        customBodyRender: (v) => eventTypeLabels[v] || v,
      },
    },
    dateFilterColumn('eventDate', t('lifeEvent.eventDate'), i18n.language),
    deadlineCol,
    {
      name: 'status', label: t('lifeEvent.status'),
      options: {
        filter: true, sort: true, filterType: 'dropdown',
        filterOptions: { defaultOp: 'eq', names: statusOptions.map((o) => o.name), renderValue: (v) => v },
        customBodyRender: (v) => statusLabels[v] || v,
      },
    },
  ], [t, i18n.language, deadlineCol, statusLabels, eventTypeLabels, statusOptions, eventTypeOptions, data]);

  return { columns, data, fetching, pagination, updateStatus, create, objectSchema, statusLabels, eventTypeLabels };
};

export default useLifeEventApi;
